/* MatchSectionsMode.js */

"use strict";

const _ = require("lodash");
const RegexUtils = require("../RegexUtils");
const AceUtils = require("../AceUtils");
const DEFAULT_SECTION_DELIMITER_REGEX = "(^[^\\s]+)";
const MATCH_ALL_REGEX = RegexUtils.MATCH_ALL_REGEX;

class MatchSectionsMode {
    static getHighlightRules(regex, sectionDelimiter) {
        if (MATCH_ALL_REGEX.indexOf(regex) !== -1) {
            return this._getMatchAllRules(sectionDelimiter);
        } else if (sectionDelimiter) {
            return this._getHighLightRulesGivenRegexAndSectionDelimiter(regex, sectionDelimiter);
        } else {
            return this._getHighlightRulesGivenOnlyRegex(regex);
        }
    }

    static _getMatchAllRules(sectionDelimiter) {
        let startRules = [],
            matchedSection1Rules = [],
            matchedSection2Rules = [];
        if (sectionDelimiter) {
            let sectionDelimiterRegex = "^" + sectionDelimiter + "\\s*$";
            startRules.push({
                regex: sectionDelimiterRegex,
                token: "none",
                next: "section_delimiters_state1",
            });
            let multipleSectionDelimitersRules1 = [
                {regex: sectionDelimiterRegex, token: "none"},
                {regex: "^\\s*" + sectionDelimiter + "?\\s*$", token: "none"},
                {regex: ".+", token: "matched_section1", next: "matched_section1"},
            ];
            let multipleSectionDelimitersRules2 = _.cloneDeep(multipleSectionDelimitersRules1);
            multipleSectionDelimitersRules2[2].token = "matched_section2";
            multipleSectionDelimitersRules2[2].next = "matched_section2";
            matchedSection1Rules.push({
                regex: sectionDelimiterRegex,
                token: "none",
                next: "section_delimiters_state2",
            });
            matchedSection2Rules.push({
                regex: sectionDelimiterRegex,
                token: "none",
                next: "section_delimiters_state1",
            });
            matchedSection1Rules.push({defaultToken: "matched_section1"});
            matchedSection2Rules.push({defaultToken: "matched_section2"});
            return {
                start: startRules,
                section_delimiters_state1: multipleSectionDelimitersRules1,
                matched_section1: matchedSection1Rules,
                section_delimiters_state2: multipleSectionDelimitersRules2,
                matched_section2: matchedSection2Rules,
            };
        } else {
            startRules.push({
                regex: DEFAULT_SECTION_DELIMITER_REGEX,
                token: "matched_section1",
                next: "matched_section1",
            });
            matchedSection1Rules.push({
                regex: DEFAULT_SECTION_DELIMITER_REGEX,
                token: "matched_section2",
                next: "matched_section2",
            });
            matchedSection2Rules.push({
                regex: DEFAULT_SECTION_DELIMITER_REGEX,
                token: "matched_section1",
                next: "matched_section1",
            });
            matchedSection1Rules.push({defaultToken: "matched_section1"});
            matchedSection2Rules.push({defaultToken: "matched_section2"});
            return {
                start: startRules,
                matched_section1: matchedSection1Rules,
                matched_section2: matchedSection2Rules,
            };
        }
    }

    static _getHighLightRulesGivenRegexAndSectionDelimiter(regex, sectionDelimiterString) {
        let sectionDelimiterRegex = "^" + sectionDelimiterString + "\\s*$";
        let regexWrappedWithParantheses = RegexUtils.isRegexWrappedWithParantheses(regex)
            ? regex
            : "(" + regex + ")";
        let startRules = [
            {
                regex: "(^.*?)" + regexWrappedWithParantheses + "(.*$)",
                token: this._returnTokenWithSectionDelimiter,
                next: "matched_section1",
            },
            {regex: sectionDelimiterRegex, token: "none", next: "section_delimiters_state1"},
            {regex: ".+", token: "none", next: "section_not_matched"},
        ];
        let sectionNotMatched = [
            {regex: sectionDelimiterRegex, token: "none", next: "section_delimiters_state1"},
        ];
        let multipleSectionDelimitersRules1 = [
            {regex: sectionDelimiterRegex, token: "none"},
            {regex: "^\\s*" + sectionDelimiterString + "?\\s*$", token: "none"},
            {
                regex: "(^.*?)" + regexWrappedWithParantheses + "(.*$)",
                token: this._returnTokenWithSectionDelimiter,
                next: "matched_section1",
            },
            {regex: ".+", token: "none", next: "section_not_matched"},
        ];
        let multipleSectionDelimitersRules2 = _.cloneDeep(multipleSectionDelimitersRules1);
        multipleSectionDelimitersRules2[2].next = "matched_section2";
        let matchedSection1Rules = [
            {regex: sectionDelimiterRegex, token: "none", next: "section_delimiters_state2"},
            {defaultToken: "matched_section1"},
        ];
        let matchedSection2Rules = [
            {regex: sectionDelimiterRegex, token: "none", next: "section_delimiters_state1"},
            {defaultToken: "matched_section2"},
        ];
        return {
            start: startRules,
            section_not_matched: sectionNotMatched,
            section_delimiters_state1: multipleSectionDelimitersRules1,
            matched_section1: matchedSection1Rules,
            section_delimiters_state2: multipleSectionDelimitersRules2,
            matched_section2: matchedSection2Rules,
        };
    }

    static _getHighlightRulesGivenOnlyRegex(regex) {
        let startRules = [],
            matchedSection1Rules = [],
            matchedSection2Rules = [];
        //if given regex doesn't start with a space, add a rule to match the regex at the beginning of the line
        let whitespaceAtBeginningOfLineRegex = new RegExp("^\\s");
        if (!whitespaceAtBeginningOfLineRegex.test(regex)) {
            let regexAtTheBeginningOfTheLine = "(^)(" + regex + ")";
            startRules.push({
                regex: regexAtTheBeginningOfTheLine,
                token: "regex",
                next: "matched_section1",
            });
            matchedSection1Rules.push(0, {
                regex: regexAtTheBeginningOfTheLine,
                token: "regex",
                next: "matched_section2",
            });
            matchedSection2Rules.push(0, {
                regex: regexAtTheBeginningOfTheLine,
                token: "regex",
                next: "matched_section1",
            });
        }
        let matchedSectionStartRegex =
            DEFAULT_SECTION_DELIMITER_REGEX.substr(0, DEFAULT_SECTION_DELIMITER_REGEX.length - 1) +
            ".*)";
        matchedSectionStartRegex += RegexUtils.isRegexWrappedWithParantheses(regex)
            ? regex
            : "(" + regex + ")";
        startRules.push({
            regex: matchedSectionStartRegex,
            token: this._returnToken,
            next: "matched_section1",
        });
        matchedSection1Rules.push({
            regex: matchedSectionStartRegex,
            token: this._returnToken,
            next: "matched_section2",
        });
        matchedSection2Rules.push({
            regex: matchedSectionStartRegex,
            token: this._returnToken,
            next: "matched_section1",
        });
        let unmatchedSectionStartRule = {
            regex: DEFAULT_SECTION_DELIMITER_REGEX,
            token: "none",
            next: "start",
        };
        matchedSection1Rules.push(unmatchedSectionStartRule);
        matchedSection2Rules.push(unmatchedSectionStartRule);
        matchedSection1Rules.push({defaultToken: "matched_section1"});
        matchedSection2Rules.push({defaultToken: "matched_section2"});
        return {
            start: startRules,
            matched_section1: matchedSection1Rules,
            matched_section2: matchedSection2Rules,
        };
    }

    static _returnToken(...args) {
        let tokenArray = [];
        tokenArray.push(this.next);
        tokenArray.push("regex");
        for (let i = 2; i < args.length; i++) {
            tokenArray.push("namedgroup_none");
        }

        return tokenArray;
    }

    static _returnTokenWithSectionDelimiter(...args) {
        let tokenArray = [];
        tokenArray.push(this.next);
        tokenArray.push("regex");
        for (let i = 2; i < args.length - 1; i++) {
            tokenArray.push("namedgroup_none");
        }
        tokenArray.push(this.next);
        return tokenArray;
    }

    static getMatchCount(editorSession) {
        let rowCount = editorSession.getLength();
        let matchCount = 0;
        let prevState = null;
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
            let currState = editorSession.getState(rowIndex);
            if (currState.startsWith("matched_section") && prevState !== currState) {
                matchCount++;
            }
            prevState = currState;
        }
        return matchCount;
    }

    static getCurrentMatchNumber(editor, regex, beforeStart = false) {
        let selectionRange = editor.getSelectionRange();
        // if beforeStart is true, get match count before start of current selection range
        // else get match count before end of current selection range
        let endPosition = beforeStart ? selectionRange.start : selectionRange.end;
        if (MATCH_ALL_REGEX.indexOf(regex) === -1) {
            return this._getMatchCountGivenRegex(editor, endPosition, regex);
        } else {
            return this._getMatchCountGivenEndPosition(editor, endPosition);
        }
    }

    static _getMatchCountGivenRegex(editor, endPosition, regex) {
        let editorSession = editor.getSession();
        let rowCount = editorSession.getLength();
        let endRow = endPosition ? endPosition.row : rowCount - 1;
        let matchCount = 0;
        let lastMatchRow;
        let prevState = null;
        for (let rowIndex = 0; rowIndex <= endRow; rowIndex++) {
            let currState = editorSession.getState(rowIndex);
            if (currState.startsWith("matched_section") && prevState !== currState) {
                // look for start of sections
                if (rowIndex === endPosition.row) {
                    let regexMatch = editorSession.getLine(rowIndex).match(regex);
                    if (regexMatch.index < endPosition.column) {
                        matchCount++;
                        lastMatchRow = rowIndex;
                    }
                } else {
                    matchCount++;
                    lastMatchRow = rowIndex;
                }
            }
            prevState = currState;
        }
        return {matchCount: matchCount, lastMatchRow: lastMatchRow};
    }

    static _getMatchCountGivenEndPosition(editor, endPosition) {
        let editorSession = editor.getSession();
        let rowCount = editorSession.getLength();
        let endRow = endPosition ? endPosition.row : rowCount - 1;
        endRow = endPosition && endPosition.column === 0 ? endRow - 1 : endRow;
        let matchCount = 0;
        let lastMatchRow;
        let prevState = null;
        for (let rowIndex = 0; rowIndex <= endRow; rowIndex++) {
            let currState = editorSession.getState(rowIndex);
            if (currState.startsWith("matched_section") && prevState !== currState) {
                // look for start of sections
                matchCount++;
                lastMatchRow = rowIndex;
            }
            prevState = currState;
        }
        return {matchCount: matchCount, lastMatchRow: lastMatchRow};
    }

    static getNextMatchNumber(
        editor,
        regex,
        currentMatchNumber,
        totalCount,
        cursorPositionChanged
    ) {
        //highlight section when totalCount = 1
        if (totalCount === 0) {
            return totalCount;
        }
        let editorSession = editor.getSession();
        if (cursorPositionChanged && totalCount > 1) {
            let matchCountDetails = this.getCurrentMatchNumber(editor, regex);
            currentMatchNumber = matchCountDetails.matchCount;
        }
        let selectionRange = editor.getSelectionRange();
        let lastRowIndex = editorSession.getLength() - 1;
        let currentRowIndex = this._getStartRowForNextMatch(editor, regex);
        let prevState = currentRowIndex === 0 ? null : editorSession.getState(currentRowIndex - 1);
        let nextMatchNumber;
        let crossedEndOnce =
            currentRowIndex === 0 && currentRowIndex !== selectionRange.end.row ? true : false;
        while (!nextMatchNumber && currentRowIndex <= lastRowIndex) {
            let currentRowState = editorSession.getState(currentRowIndex);
            let newSectionStart =
                currentRowState.startsWith("matched_section") && prevState !== currentRowState;
            if (newSectionStart) {
                nextMatchNumber = crossedEndOnce || totalCount === 1 ? 1 : currentMatchNumber + 1;
                this._highlightMatch(currentRowIndex, editor, regex);
            }
            currentRowIndex++;
            prevState = currentRowState;
            //do not repeat searching entire text more than once - avoid infinite loop
            if (currentRowIndex > lastRowIndex && !crossedEndOnce) {
                currentRowIndex = 0;
                crossedEndOnce = true;
                prevState = null;
            }
        }
        return nextMatchNumber;
    }

    static getPreviousMatchNumber(
        editor,
        regex,
        currentMatchNumber,
        totalCount,
        cursorPositionChanged = false
    ) {
        if (totalCount === 0) {
            return totalCount;
        }
        let editorSession = editor.getSession();
        if (cursorPositionChanged && totalCount > 1) {
            let matchCountDetails = this.getCurrentMatchNumber(editor, regex, true);
            if (matchCountDetails.lastMatchRow !== undefined) {
                this._highlightMatch(matchCountDetails.lastMatchRow, editor, regex);
                return matchCountDetails.matchCount;
            }
            currentMatchNumber = matchCountDetails.matchCount;
        }
        let selectionRange = editor.getSelectionRange();
        let lastRowIndex = editorSession.getLength() - 1;
        let currentRowIndex;
        if (MATCH_ALL_REGEX.indexOf(regex) === -1) {
            let regexMatch = this._isValidMatch(
                editorSession.getLine(selectionRange.start.row),
                regex
            );
            currentRowIndex = selectionRange.start.row;
            if (
                regexMatch &&
                ((regexMatch[0] === editor.getSelectedText() &&
                    regexMatch.index === selectionRange.start.column) ||
                    selectionRange.start.column <= regexMatch.index)
            ) {
                currentRowIndex = this._getPreviousRow(selectionRange.start.row, lastRowIndex);
            }
        } else {
            currentRowIndex = this._getPreviousRow(selectionRange.start.row, lastRowIndex);
        }
        let previousMatchNumber;
        let crossedStartOnce =
            selectionRange.start.row !== currentRowIndex && selectionRange.start.row === 0
                ? true
                : false;
        let previousRowState = editorSession.getState(currentRowIndex);
        let currentRowState;
        while (!previousMatchNumber && currentRowIndex >= 0) {
            currentRowState = previousRowState;
            previousRowState =
                currentRowIndex === 0 ? null : editorSession.getState(currentRowIndex - 1);
            if (
                currentRowState.startsWith("matched_section") &&
                currentRowState !== previousRowState
            ) {
                previousMatchNumber =
                    crossedStartOnce || totalCount === 1 ? totalCount : currentMatchNumber - 1;
                this._highlightMatch(currentRowIndex, editor, regex);
            }
            currentRowIndex--;
            if (currentRowIndex < 0 && !crossedStartOnce) {
                currentRowIndex = lastRowIndex;
                crossedStartOnce = true;
                previousRowState = editorSession.getState(currentRowIndex);
            }
        }
        return previousMatchNumber;
    }

    static _isValidMatch(text, regex) {
        if (!text || !regex) {
            return false;
        }
        return text.match(regex);
    }

    static _getNextRow(rowIndex, lastRowIndex) {
        return rowIndex < lastRowIndex ? rowIndex + 1 : 0;
    }

    static _getPreviousRow(rowIndex, lastRowIndex) {
        return rowIndex === 0 ? lastRowIndex : rowIndex - 1;
    }

    static _getStartRowForNextMatch(editor, regex) {
        let editorSession = editor.getSession();
        let selectionRange = editor.getSelectionRange();
        let lastRowIndex = editorSession.getLength() - 1;
        let currentRowIndex;
        if (MATCH_ALL_REGEX.indexOf(regex) === -1) {
            currentRowIndex = selectionRange.end.row;
            let regexTokenDetails = this._getRegexToken(editorSession, regex, currentRowIndex);
            currentRowIndex =
                regexTokenDetails && regexTokenDetails.start < selectionRange.end.column
                    ? this._getNextRow(currentRowIndex, lastRowIndex)
                    : currentRowIndex;
        } else {
            currentRowIndex =
                selectionRange.end.column === 0
                    ? selectionRange.end.row
                    : this._getNextRow(selectionRange.end.row, lastRowIndex);
        }
        return currentRowIndex;
    }

    static _highlightMatch(matchStartRow, editor, regex) {
        if (MATCH_ALL_REGEX.indexOf(regex) === -1) {
            let editorSession = editor.getSession();
            let regexToken = this._getRegexToken(editorSession, regex, matchStartRow);
            if (regexToken) {
                let newSelectionRange = AceUtils.getRangeInstance(
                    matchStartRow,
                    regexToken.start,
                    matchStartRow,
                    regexToken.start + regexToken.value.length
                );
                editor.revealRange(newSelectionRange);
            }
        } else {
            this._highlightSection({row: matchStartRow, column: 0}, editor);
        }
    }

    static _highlightSection(sectionStartPosition, editor) {
        let endOfSection = this._getEndOfSectionPosition(sectionStartPosition, editor.getSession());
        let currentSectionRange = AceUtils.getRangeInstance(
            sectionStartPosition.row,
            sectionStartPosition.column,
            endOfSection.row,
            endOfSection.column
        );
        editor.revealRange(currentSectionRange);
    }

    static _getEndOfSectionPosition(sectionStartPosition, editorSession) {
        let lastRowIndex = editorSession.getLength();
        let currentSectionState = editorSession.getState(sectionStartPosition.row);
        let rowIndex = sectionStartPosition.row;
        while (rowIndex <= lastRowIndex) {
            let currentRowState = editorSession.getState(rowIndex);
            if (currentSectionState !== currentRowState) {
                break;
            } else {
                rowIndex++;
            }
        }
        //consider the case where last row is given as section start & hence last row becomes start & end of current section
        return {row: rowIndex - 1, column: editorSession.getLine(rowIndex - 1).length};
    }

    static _getRegexToken(editorSession, regex, currentRowIndex) {
        let tokens = editorSession.getTokens(currentRowIndex);
        let regexToken = tokens.find(function (token) {
            return token.type === "regex";
        });
        if (!regexToken) {
            return null;
        }
        if (_.has(regexToken, "start")) {
            return regexToken;
        }
        let regexIndex = -1;
        let startIndex = 0;
        let currentRowText = editorSession.getLine(currentRowIndex);
        while ((regexIndex = currentRowText.indexOf(regexToken.value, startIndex)) !== -1) {
            let tokenAtRegexIndex = editorSession.getTokenAt(
                currentRowIndex,
                regexIndex === 0 ? regexIndex : regexIndex + 1
            );
            if (tokenAtRegexIndex.type === "regex") {
                regexToken.start = regexIndex;
                break;
            }
            startIndex = regexIndex + 1;
        }
        return regexToken;
    }
}

module.exports = MatchSectionsMode;
